import { Button, Cell, Row } from '@dexma/ui-components';
import { ChartLimitValue, ConfigFieldType, FieldStatus, FormFields } from 'adapter/in/common/form/types';
import { OPTIONS } from 'adapter/in/common/widget/options';
import { WidgetOptionValues } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Save/useSaveWidgetContainer';
import { buildTextInputFieldData } from 'adapter/in/common/form/Text';
import useChartLimitContainer from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/ChartLimit/useChartLimitContainer';
import { StyledResetButton } from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Group/style';
import {
    StyledRangeContainer,
    StyledRemoveElementContainer,
    StyledResetRangeContainer,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options/Range/style';
import ChartLimitGroupContainer from 'adapter/in/dashboards/pages/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/OptionForm/ChartLimit';
import { asWidgetChartLimit, JsonChartLimit } from 'adapter/out/client/widgets/data/mappers/common';
import i18n from 'asset/translation';
import Widget from 'domain/entity/widget';
import { ColorType } from 'domain/entity/widgetData';
import ChartData from 'domain/entity/widgets/ChartData';
import GaugeData, { WidgetChartLimit } from 'domain/entity/widgets/GaugeData';
import { pipe } from 'fp-ts/lib/function';
import React, { ReactElement } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { isChartData, isGaugeData } from 'adapter/in/common/form/populateForm';

const asChartLimitValues = (key: string, value: ChartLimitValue[]) => ({
    key,
    value: value.map((chartLimit) => ({
        name: chartLimit.name.value,
        color: {
            type: ColorType.BY_DEFAULT,
            by_default: chartLimit.color.value,
        },
        value: Number(chartLimit.value.value),
    })),
});

export const populateChartLimitValues = (getValues: UseFormGetValues<FormFields>): WidgetOptionValues[] => {
    if (getValues(OPTIONS.CHECKPOINTS) !== undefined) {
        const arrayValues = pipe(getValues(OPTIONS.CHECKPOINTS), (chartLimitsValues) =>
            chartLimitHaveOptionalData((chartLimitsValues as unknown) as ChartLimitValue[])
        );
        if (arrayValues.length > 0) {
            return [asChartLimitValues(OPTIONS.CHECKPOINTS, (arrayValues as unknown) as ChartLimitValue[])];
        }
    }
    return [];
};

export const chartLimitHaveOptionalData = (chartLimits: ChartLimitValue[]): ChartLimitValue[] => {
    const haveOptionalData =
        chartLimits
            .map((chartLimit) => Object.values(chartLimit))
            .flat()
            .filter((value) => value.status === FieldStatus.HAS_VALUE).length > 0;
    return haveOptionalData ? chartLimits : [];
};

type ChartLimitMapperObject = {
    isDefaultData: boolean;
    chartLimits: WidgetChartLimit[];
};

export const getCustomChartLimit = (widget: Widget, widgetData?: ChartData | GaugeData): ChartLimitMapperObject => {
    const hasNoDefaultChartLimits = widget.optionFields.filter((option) => option.key === OPTIONS.CHECKPOINTS).length === 0;
    if (widgetData && isGaugeData(widgetData) && hasNoDefaultChartLimits) {
        return { isDefaultData: true, chartLimits: widgetData.checkpoints };
    }
    if (widgetData && isChartData(widgetData) && hasNoDefaultChartLimits) {
        return { isDefaultData: true, chartLimits: [] };
    }
    return {
        isDefaultData: false,
        chartLimits: asWidgetChartLimit(
            widget.optionFields.filter((option) => option.key === OPTIONS.CHECKPOINTS).length > 0
                ? ((widget.optionFields.filter((option) => option.key === OPTIONS.CHECKPOINTS)[0].value as unknown) as JsonChartLimit[])
                : []
        ),
    };
};

export const mapChartChartLimitFieldData = ({ isDefaultData, chartLimits }: ChartLimitMapperObject): ChartLimitValue[] => {
    const status = isDefaultData ? FieldStatus.DEFAULT_DATA : FieldStatus.HAS_VALUE;
    return chartLimits.map((chartLimit) => ({
        name: buildTextInputFieldData(status, chartLimit.name, ConfigFieldType.OPTIONS, chartLimit.name),
        color: buildTextInputFieldData(status, chartLimit.color.byDefault, ConfigFieldType.OPTIONS, chartLimit.color.byDefault),
        value: buildTextInputFieldData(status, String(chartLimit.value), ConfigFieldType.OPTIONS, String(chartLimit.value)),
    }));
};

type ChartLimitContainerProps = {
    data?: GaugeData;
    title: string;
};

const ChartLimitContainer = ({ data, title }: ChartLimitContainerProps): ReactElement => {
    const { fields, showReset, handleAddLimit, handleResetLimits, handleRemoveLimit } = useChartLimitContainer(OPTIONS.CHECKPOINTS, data);

    return (
        <StyledRangeContainer key='chartLimits'>
            <Row>
                <Cell className='widget-type-block'>
                    <span>{title}</span>
                </Cell>
                {showReset && (
                    <StyledResetRangeContainer>
                        <StyledResetButton
                            type='button'
                            variant='link'
                            text={i18n.t('widgets.widgetMenu.resetDefaultData')}
                            data-testid='cancel'
                            onClick={handleResetLimits}
                        />
                    </StyledResetRangeContainer>
                )}
            </Row>
            {fields.length > 0 &&
                fields.map((item, index) => (
                    <ChartLimitGroupContainer key={item.id} item={item} index={index} handleRemoveLimit={handleRemoveLimit} />
                ))}
            <Row>
                <StyledRemoveElementContainer>
                    <Button
                        iconBefore='add'
                        text={i18n.t('widgets.widgetMenu.checkpoints.add')}
                        variant='outline'
                        onClick={handleAddLimit}
                    />
                </StyledRemoveElementContainer>
            </Row>
        </StyledRangeContainer>
    );
};

export default ChartLimitContainer;
